import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Message } from "../components/Message";
import { useDataContext } from "../context/DataContext";
import { useViewPreferences, type CompetenciesViewMode } from "../context/ViewPreferencesContext";
import "./Styles/CompetenciesPage.css";

const DEFAULT_PERSONNEL_ID = "123bc4ca-6e3a-4f59-b935-aba72a000223";

// ============================================================================
// SHARED TYPES AND UTILITIES
// ============================================================================

interface Competency {
  id: string;
  competencyId: string;
  type: string;
  expires: string;
  expiresDate: Date | null;
  category: string;
  parentId: string | null;
  isExpired?: boolean;
}

interface CompetencyGroup {
  parent: Competency;
  children: Competency[];
}

type CompetencyItem = 
  | { type: "standalone"; competency: Competency }
  | { type: "group"; group: CompetencyGroup };

type ExpirationStatus = "ok" | "expiring-60" | "expiring-30" | "expired";

interface SupervisedPerson {
  id: string;
  name: string;
  competencies: Competency[];
}

interface QueryGroup {
  id: string;
  competencies: string[];
  internalOperator: "and" | "or";
}

interface QueryState {
  groups: QueryGroup[];
  connectors: ("and" | "or")[];
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const pickString = (record: UnknownRecord, keys: string[]): string | null => {
  for (const key of keys) {
    const value = record[key];
    if (value === null || value === undefined) continue;
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
  }
  return null;
};

const extractRows = (data: unknown): UnknownRecord[] => {
  if (Array.isArray(data)) {
    return data.filter(isRecord);
  }
  if (isRecord(data)) {
    const possibleArray = data["value"];
    if (Array.isArray(possibleArray)) {
      return possibleArray.filter(isRecord);
    }
    const dataField = data["data"];
    if (Array.isArray(dataField)) {
      return dataField.filter(isRecord);
    }
    if (isRecord(dataField)) {
      const nestedValue = dataField["value"];
      if (Array.isArray(nestedValue)) {
        return nestedValue.filter(isRecord);
      }
    }
    return [data];
  }
  return [];
};

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date;
}

function formatDateDDMMYYYY(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function getExpirationStatus(expiresDate: Date | null): ExpirationStatus {
  if (!expiresDate) return "ok";
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(expiresDate.getFullYear(), expiresDate.getMonth(), expiresDate.getDate());
  
  const diffTime = expiry.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return "expired";
  if (diffDays <= 30) return "expiring-30";
  if (diffDays <= 60) return "expiring-60";
  return "ok";
}

function isCompetencyExpired(expiresDate: Date | null): boolean {
  if (!expiresDate) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(expiresDate.getFullYear(), expiresDate.getMonth(), expiresDate.getDate());
  return expiry.getTime() < today.getTime();
}

function ExpirationIcon({ status }: { status: ExpirationStatus }) {
  if (status === "ok") return null;
  
  if (status === "expired") {
    return (
      <span className="expiration-icon expiration-icon--expired" title="Expired">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      </span>
    );
  }
  
  if (status === "expiring-30") {
    return (
      <span className="expiration-icon expiration-icon--warning" title="Expires within 30 days">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </span>
    );
  }
  
  if (status === "expiring-60") {
    return (
      <span className="expiration-icon expiration-icon--clock" title="Expires within 60 days">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      </span>
    );
  }
  
  return null;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// Helper to extract single record from root data
const extractRootPerson = (data: unknown): UnknownRecord | null => {
  if (!data) return null;
  if (isRecord(data)) {
    const dataValue = data["data"];
    if (isRecord(dataValue)) {
      if (Array.isArray(dataValue["value"])) {
        const arr = dataValue["value"].filter(isRecord);
        return arr[0] ?? null;
      }
      if (dataValue["PersonnelId"] || dataValue["Id"] || dataValue["GivenNames"]) {
        return dataValue;
      }
    }
    if (data["PersonnelId"] || data["Id"] || data["GivenNames"]) {
      return data;
    }
  }
  return null;
};

function parseSupervisedData(data: unknown, rootPersonnelId: string): SupervisedPerson[] {
  const rows = extractRows(data);
  const normalizedRootId = rootPersonnelId.toLowerCase();

  const people: SupervisedPerson[] = rows.map((person, idx) => {
    const given = (pickString(person, ["GivenNames", "GivenName"]) || "").trim();
    const surname = (pickString(person, ["Surname", "LastName"]) || "").trim();
    const name = [given, surname].filter(Boolean).join(" ").trim() || `Person ${idx + 1}`;
    const id = pickString(person, ["Id", "PersonnelId"]) ?? `person-${idx}`;

    const competenciesRaw = person["Competencies"];
    const competencies: Competency[] = [];
    if (Array.isArray(competenciesRaw)) {
      competenciesRaw.forEach((comp) => {
        if (!isRecord(comp)) return;
        const expiresRaw = pickString(comp, ["Expires"]);
        const expiresDate = parseDate(expiresRaw);
        competencies.push({
          id: pickString(comp, ["PersonnelCompetencyId", "Id"]) ?? `comp-${competencies.length}`,
          competencyId: pickString(comp, ["CompetencyId"]) ?? "",
          type: pickString(comp, ["Type"]) ?? "—",
          expires: formatDateDDMMYYYY(expiresRaw),
          expiresDate,
          category: pickString(comp, ["Category"]) ?? "",
          parentId: pickString(comp, ["ParentId"]),
          isExpired: isCompetencyExpired(expiresDate)
        });
      });
    }

    competencies.sort((a, b) => {
      const aStatus = getExpirationStatus(a.expiresDate);
      const bStatus = getExpirationStatus(b.expiresDate);
      const aExpired = aStatus === "expired";
      const bExpired = bStatus === "expired";
      
      if (aExpired && !bExpired) return -1;
      if (!aExpired && bExpired) return 1;
      
      return a.type.localeCompare(b.type, undefined, { sensitivity: "base" });
    });

    return { id, name, competencies };
  });

  people.sort((a, b) => {
    const aIsRoot = a.id.toLowerCase() === normalizedRootId;
    const bIsRoot = b.id.toLowerCase() === normalizedRootId;
    if (aIsRoot && !bIsRoot) return -1;
    if (!aIsRoot && bIsRoot) return 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });

  return people;
}

function groupCompetencies(competencies: Competency[]): CompetencyItem[] {
  const competencyIdSet = new Set<string>();
  competencies.forEach(comp => {
    if (comp.competencyId) {
      competencyIdSet.add(comp.competencyId);
    }
  });

  const childrenByParent = new Map<string, Competency[]>();
  competencies.forEach(comp => {
    if (comp.parentId && competencyIdSet.has(comp.parentId)) {
      const existing = childrenByParent.get(comp.parentId) ?? [];
      existing.push(comp);
      childrenByParent.set(comp.parentId, existing);
    }
  });

  const items: CompetencyItem[] = [];
  competencies.forEach(comp => {
    if (comp.parentId && competencyIdSet.has(comp.parentId)) return;

    const children = childrenByParent.get(comp.competencyId);
    if (children && children.length > 0) {
      children.sort((a, b) => {
        const aStatus = getExpirationStatus(a.expiresDate);
        const bStatus = getExpirationStatus(b.expiresDate);
        const aExpired = aStatus === "expired";
        const bExpired = bStatus === "expired";
        if (aExpired && !bExpired) return -1;
        if (!aExpired && bExpired) return 1;
        return a.type.localeCompare(b.type, undefined, { sensitivity: "base" });
      });
      items.push({ type: "group", group: { parent: comp, children } });
    } else {
      items.push({ type: "standalone", competency: comp });
    }
  });

  items.sort((a, b) => {
    const getWorstStatus = (item: CompetencyItem): ExpirationStatus => {
      if (item.type === "standalone") {
        return getExpirationStatus(item.competency.expiresDate);
      }
      let worst: ExpirationStatus = getExpirationStatus(item.group.parent.expiresDate);
      for (const child of item.group.children) {
        const childStatus = getExpirationStatus(child.expiresDate);
        if (childStatus === "expired") return "expired";
        if (childStatus === "expiring-30" && worst !== "expired") worst = "expiring-30";
        if (childStatus === "expiring-60" && worst === "ok") worst = "expiring-60";
      }
      return worst;
    };

    const aStatus = getWorstStatus(a);
    const bStatus = getWorstStatus(b);
    const aExpired = aStatus === "expired";
    const bExpired = bStatus === "expired";
    
    if (aExpired && !bExpired) return -1;
    if (!aExpired && bExpired) return 1;

    const aType = a.type === "standalone" ? a.competency.type : a.group.parent.type;
    const bType = b.type === "standalone" ? b.competency.type : b.group.parent.type;
    return aType.localeCompare(bType, undefined, { sensitivity: "base" });
  });

  return items;
}

// ============================================================================
// LIST VIEW COMPONENT
// ============================================================================

function ListView({
  supervisedPeople,
  selectedPersonId,
  effectivePersonId,
  competencySearch,
  isSupervisor,
  onSelectPerson,
  onSearchChange
}: {
  supervisedPeople: SupervisedPerson[];
  selectedPersonId: string | null;
  effectivePersonId: string | null;
  competencySearch: string;
  isSupervisor: boolean;
  onSelectPerson: (id: string) => void;
  onSearchChange: (search: string) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedGroups(new Set());
  }, [selectedPersonId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setDropdownSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dropdownOpen]);

  const selectedPerson = useMemo(() => {
    return supervisedPeople.find(p => p.id === effectivePersonId) ?? null;
  }, [supervisedPeople, effectivePersonId]);

  const filteredDropdownPeople = useMemo(() => {
    if (!dropdownSearch.trim()) return supervisedPeople;
    const term = dropdownSearch.toLowerCase();
    return supervisedPeople.filter(p => p.name.toLowerCase().includes(term));
  }, [supervisedPeople, dropdownSearch]);

  const groupedCompetencies = useMemo(() => {
    if (!selectedPerson) return [];
    return groupCompetencies(selectedPerson.competencies);
  }, [selectedPerson]);

  const filteredGroupedCompetencies = useMemo(() => {
    if (!competencySearch.trim()) return groupedCompetencies;
    const term = competencySearch.toLowerCase();
    
    return groupedCompetencies.filter(item => {
      if (item.type === "standalone") {
        const c = item.competency;
        return c.type.toLowerCase().includes(term) || 
               c.expires.toLowerCase().includes(term) ||
               c.category.toLowerCase().includes(term);
      } else {
        const parent = item.group.parent;
        const parentMatches = parent.type.toLowerCase().includes(term) || 
                              parent.expires.toLowerCase().includes(term) ||
                              parent.category.toLowerCase().includes(term);
        const anyChildMatches = item.group.children.some(c => 
          c.type.toLowerCase().includes(term) || 
          c.expires.toLowerCase().includes(term) ||
          c.category.toLowerCase().includes(term)
        );
        return parentMatches || anyChildMatches;
      }
    });
  }, [groupedCompetencies, competencySearch]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const expandAllGroups = () => {
    const allGroupIds = groupedCompetencies
      .filter(item => item.type === "group")
      .map(item => (item as { type: "group"; group: CompetencyGroup }).group.parent.id);
    setExpandedGroups(new Set(allGroupIds));
  };

  const collapseAllGroups = () => {
    setExpandedGroups(new Set());
  };

  const handleSelectPerson = (id: string) => {
    onSelectPerson(id);
    setDropdownOpen(false);
    setDropdownSearch("");
    onSearchChange("");
  };

  return (
    <>
      <div className="competencies-header">
        {isSupervisor ? (
          <div className="competencies-header__person-select" ref={dropdownRef}>
            <button
              type="button"
              className="competencies-person-dropdown"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
            >
              <span className="competencies-person-dropdown__text">
                {selectedPerson?.name ?? "Select person..."}
              </span>
              <span className="competencies-person-dropdown__arrow">
                {dropdownOpen ? "▲" : "▼"}
              </span>
            </button>
            {dropdownOpen && (
              <div className="competencies-dropdown-menu">
                <div className="competencies-dropdown-search">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={dropdownSearch}
                    onChange={(e) => setDropdownSearch(e.target.value)}
                    className="competencies-dropdown-search__input"
                  />
                </div>
                <div className="competencies-dropdown-list">
                  {filteredDropdownPeople.length === 0 ? (
                    <div className="competencies-dropdown-empty">No matches found</div>
                  ) : (
                    filteredDropdownPeople.map(person => (
                      <button
                        key={person.id}
                        type="button"
                        className={`competencies-dropdown-item${person.id === selectedPersonId ? " competencies-dropdown-item--selected" : ""}`}
                        onClick={() => handleSelectPerson(person.id)}
                      >
                        <span className="competencies-dropdown-item__name">{person.name}</span>
                        <span className="competencies-dropdown-item__count">
                          {person.competencies.length}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="competencies-header__person-name">
            {selectedPerson?.name ?? "Loading..."}
          </div>
        )}
        <div className="competencies-header__search">
          <input
            type="text"
            placeholder="Search"
            value={competencySearch}
            onChange={(e) => onSearchChange(e.target.value)}
            className="competencies-filter-input"
          />
        </div>
      </div>

      {selectedPerson && selectedPerson.competencies.length > 0 && filteredGroupedCompetencies.length > 0 && (
        <>
          <div className="competencies-table__header">
            <div className="competencies-table__cell competencies-table__cell--expand">
              <button
                type="button"
                className="competencies-expand-btn"
                onClick={expandAllGroups}
                aria-label="Expand all"
                title="Expand all"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 6 6 5 6-5" />
                  <path d="m6 13 6 5 6-5" />
                </svg>
              </button>
            </div>
            <div className="competencies-table__cell competencies-table__cell--status">
              <button
                type="button"
                className="competencies-expand-btn"
                onClick={collapseAllGroups}
                aria-label="Collapse all"
                title="Collapse all"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 11 6-5 6 5" />
                  <path d="m6 18 6-5 6 5" />
                </svg>
              </button>
            </div>
            <div className="competencies-table__cell competencies-table__cell--type">Type</div>
            <div className="competencies-table__cell competencies-table__cell--expires">Expires</div>
          </div>
        </>
      )}

      <div className="competencies-content">
        {!selectedPerson && (
          <Message type="info" text="Select a person to view their competencies." />
        )}
        {selectedPerson && (
          <div className="competencies-list">
            {selectedPerson.competencies.length === 0 ? (
              <div className="competencies-empty">No competencies recorded</div>
            ) : filteredGroupedCompetencies.length === 0 ? (
              <div className="competencies-empty">No matching competencies found</div>
            ) : (
              <div className="competencies-table">
                {filteredGroupedCompetencies.map((item, index) => {
                  const stripe = index % 2 === 0 ? "competencies-table__row--even" : "competencies-table__row--odd";
                  if (item.type === "standalone") {
                    const comp = item.competency;
                    const status = getExpirationStatus(comp.expiresDate);
                    return (
                      <div 
                        key={comp.id} 
                        className={`competencies-table__row ${stripe}${status !== "ok" ? ` competencies-table__row--${status}` : ""}`}
                      >
                        <div className="competencies-table__cell competencies-table__cell--expand"></div>
                        <div className="competencies-table__cell competencies-table__cell--status">
                          <ExpirationIcon status={status} />
                        </div>
                        <div className="competencies-table__cell competencies-table__cell--type">{comp.type}</div>
                        <div className="competencies-table__cell competencies-table__cell--expires">{comp.expires}</div>
                      </div>
                    );
                  } else {
                    const { parent, children } = item.group;
                    const isExpanded = expandedGroups.has(parent.id);
                    const parentStatus = getExpirationStatus(parent.expiresDate);
                    
                    const hasChildWarning = children.some(c => {
                      const s = getExpirationStatus(c.expiresDate);
                      return s === "expired" || s === "expiring-30" || s === "expiring-60";
                    });
                    
                    return (
                      <div key={parent.id} className="competencies-group">
                        <div 
                          className={`competencies-table__row competencies-table__row--group ${stripe}${parentStatus !== "ok" ? ` competencies-table__row--${parentStatus}` : ""}${hasChildWarning && parentStatus === "ok" ? " competencies-table__row--has-warning" : ""}`}
                          onClick={() => toggleGroup(parent.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleGroup(parent.id); }}
                        >
                          <div className="competencies-table__cell competencies-table__cell--expand">
                            <span className={`competencies-expand-icon${isExpanded ? " competencies-expand-icon--expanded" : ""}`}>
                              ▶
                            </span>
                          </div>
                          <div className="competencies-table__cell competencies-table__cell--status">
                            <ExpirationIcon status={parentStatus} />
                          </div>
                          <div className="competencies-table__cell competencies-table__cell--type">
                            {parent.type}
                            <span className="competencies-group-count">({children.length})</span>
                          </div>
                          <div className="competencies-table__cell competencies-table__cell--expires">{parent.expires}</div>
                        </div>
                        {isExpanded && (
                          <div className="competencies-group__children">
                            {children.map((child, childIndex) => {
                              const childStatus = getExpirationStatus(child.expiresDate);
                              const childStripe = childIndex % 2 === 0 ? "competencies-table__row--odd" : "competencies-table__row--even";
                              return (
                                <div 
                                  key={child.id} 
                                  className={`competencies-table__row competencies-table__row--child ${childStripe}${childStatus !== "ok" ? ` competencies-table__row--${childStatus}` : ""}`}
                                >
                                  <div className="competencies-table__cell competencies-table__cell--expand"></div>
                                  <div className="competencies-table__cell competencies-table__cell--status">
                                    <ExpirationIcon status={childStatus} />
                                  </div>
                                  <div className="competencies-table__cell competencies-table__cell--type">{child.type}</div>
                                  <div className="competencies-table__cell competencies-table__cell--expires">{child.expires}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// SEARCH VIEW COMPONENT
// ============================================================================

function SearchView({
  supervisedPeople,
  uniqueCompetencyTypes,
  selectedCompetencyType,
  selectedCompetencyTypes,
  onSelectType,
  onAddCompetency,
  onRemoveCompetency
}: {
  supervisedPeople: SupervisedPerson[];
  uniqueCompetencyTypes: string[];
  selectedCompetencyType: string | null;
  selectedCompetencyTypes: string[];
  onSelectType: (type: string | null) => void;
  onAddCompetency: () => void;
  onRemoveCompetency: (type: string) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setDropdownSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dropdownOpen]);

  const filteredCompetencyTypes = useMemo(() => {
    let types = uniqueCompetencyTypes.filter(type => !selectedCompetencyTypes.includes(type));
    if (dropdownSearch.trim()) {
      const term = dropdownSearch.toLowerCase();
      types = types.filter(type => type.toLowerCase().includes(term));
    }
    return types;
  }, [uniqueCompetencyTypes, dropdownSearch, selectedCompetencyTypes]);

  const peopleWithAllCompetencies = useMemo(() => {
    if (selectedCompetencyTypes.length === 0) return [];
    const results: { personId: string; personName: string }[] = [];
    supervisedPeople.forEach(person => {
      const validCompTypes = new Set(
        person.competencies
          .filter(c => !c.isExpired && !c.parentId)
          .map(c => c.type)
      );
      const hasAll = selectedCompetencyTypes.every(type => validCompTypes.has(type));
      if (hasAll) {
        results.push({
          personId: person.id,
          personName: person.name
        });
      }
    });
    results.sort((a, b) => a.personName.localeCompare(b.personName, undefined, { sensitivity: "base" }));
    return results;
  }, [supervisedPeople, selectedCompetencyTypes]);

  const getValidCompetencyCount = (type: string): number => {
    return supervisedPeople.filter(p => 
      p.competencies.some(c => c.type === type && !c.isExpired && !c.parentId)
    ).length;
  };

  const handleSelectCompetency = (type: string) => {
    onSelectType(type);
    setDropdownOpen(false);
    setDropdownSearch("");
  };

  return (
    <>
      <div className="competencies-header">
        <div className="competencies-header__person-select" ref={dropdownRef}>
          <button
            type="button"
            className="competencies-person-dropdown"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-expanded={dropdownOpen}
          >
            <span className="competencies-person-dropdown__text">
              {selectedCompetencyType ?? "Select competency..."}
            </span>
            <span className="competencies-person-dropdown__arrow">
              {dropdownOpen ? "▲" : "▼"}
            </span>
          </button>
          {dropdownOpen && (
            <div className="competencies-dropdown-menu">
              <div className="competencies-dropdown-search">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  value={dropdownSearch}
                  onChange={(e) => setDropdownSearch(e.target.value)}
                  className="competencies-dropdown-search__input"
                />
              </div>
              <div className="competencies-dropdown-list">
                {filteredCompetencyTypes.length === 0 ? (
                  <div className="competencies-dropdown-empty">No matches found</div>
                ) : (
                  filteredCompetencyTypes.map(type => (
                    <button
                      key={type}
                      type="button"
                      className={`competencies-dropdown-item${type === selectedCompetencyType ? " competencies-dropdown-item--selected" : ""}`}
                      onClick={() => handleSelectCompetency(type)}
                    >
                      <span className="competencies-dropdown-item__name">{type}</span>
                      <span className="competencies-dropdown-item__count">
                        {getValidCompetencyCount(type)}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="competencies-add-btn"
          onClick={onAddCompetency}
          disabled={!selectedCompetencyType || selectedCompetencyTypes.includes(selectedCompetencyType)}
          title="Add competency to filter"
        >
          +
        </button>
      </div>

      {selectedCompetencyTypes.length > 0 && (
        <div className="competencies-selected-list">
          {selectedCompetencyTypes.map(type => (
            <div key={type} className="competencies-selected-tag">
              <span className="competencies-selected-tag__text">{type}</span>
              <button
                type="button"
                className="competencies-selected-tag__remove"
                onClick={() => onRemoveCompetency(type)}
                aria-label={`Remove ${type}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedCompetencyTypes.length > 0 && peopleWithAllCompetencies.length > 0 && (
        <div className="competencies-table__header">
          <div className="competencies-table__cell competencies-table__cell--type">Name</div>
        </div>
      )}

      <div className="competencies-content">
        {selectedCompetencyTypes.length === 0 && (
          <div className="competencies-instructions">
            <p>Select and add a set of competencies to view a list of suitable personnel.</p>
            <p>Once you have selected a set of competencies, you will see a list of personnel who possess all the specified competencies.</p>
            <p><em>Note: Expired competencies are automatically excluded from search results.</em></p>
          </div>
        )}
        {selectedCompetencyTypes.length > 0 && (
          <div className="competencies-list">
            {peopleWithAllCompetencies.length === 0 ? (
              <div className="competencies-empty">No personnel with all selected competencies (excluding expired)</div>
            ) : (
              <div className="competencies-table">
                {peopleWithAllCompetencies.map((person) => (
                  <div key={person.personId} className="competencies-table__row">
                    <div className="competencies-table__cell competencies-table__cell--type">{person.personName}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// ADVANCED SEARCH VIEW COMPONENT
// ============================================================================

function CompetencyDropdown({
  availableTypes,
  supervisedPeople,
  onSelect,
  placeholder = "Select competency..."
}: {
  availableTypes: string[];
  supervisedPeople: SupervisedPerson[];
  onSelect: (type: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const filteredTypes = useMemo(() => {
    if (!search.trim()) return availableTypes;
    const term = search.toLowerCase();
    return availableTypes.filter(type => type.toLowerCase().includes(term));
  }, [availableTypes, search]);

  const getValidCount = (type: string): number => {
    return supervisedPeople.filter(p => 
      p.competencies.some(c => c.type === type && !c.isExpired && !c.parentId)
    ).length;
  };

  const handleSelect = (type: string) => {
    onSelect(type);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="adv-dropdown" ref={containerRef}>
      <button
        type="button"
        className="adv-dropdown__trigger"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="adv-dropdown__text">{placeholder}</span>
        <span className="adv-dropdown__arrow">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="adv-dropdown__menu">
          <div className="adv-dropdown__search">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="adv-dropdown__search-input"
            />
          </div>
          <div className="adv-dropdown__list">
            {filteredTypes.length === 0 ? (
              <div className="adv-dropdown__empty">No matches found</div>
            ) : (
              filteredTypes.map(type => (
                <button
                  key={type}
                  type="button"
                  className="adv-dropdown__item"
                  onClick={() => handleSelect(type)}
                >
                  <span className="adv-dropdown__item-name">{type}</span>
                  <span className="adv-dropdown__item-count">
                    {getValidCount(type)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OperatorToggle({
  value,
  onChange,
  size = "normal"
}: {
  value: "and" | "or";
  onChange: (val: "and" | "or") => void;
  size?: "normal" | "large";
}) {
  return (
    <div className={`adv-operator-toggle adv-operator-toggle--${size}`}>
      <button
        type="button"
        className={`adv-operator-toggle__btn${value === "and" ? " adv-operator-toggle__btn--active" : ""}`}
        onClick={() => onChange("and")}
      >
        AND
      </button>
      <button
        type="button"
        className={`adv-operator-toggle__btn${value === "or" ? " adv-operator-toggle__btn--active" : ""}`}
        onClick={() => onChange("or")}
      >
        OR
      </button>
    </div>
  );
}

function QueryGroupComponent({
  group,
  groupIndex,
  availableTypes,
  supervisedPeople,
  onAddCompetency,
  onRemoveCompetency,
  onChangeOperator,
  onRemoveGroup,
  canRemove
}: {
  group: QueryGroup;
  groupIndex: number;
  availableTypes: string[];
  supervisedPeople: SupervisedPerson[];
  onAddCompetency: (groupId: string, type: string) => void;
  onRemoveCompetency: (groupId: string, type: string) => void;
  onChangeOperator: (groupId: string, operator: "and" | "or") => void;
  onRemoveGroup: (groupId: string) => void;
  canRemove: boolean;
}) {
  const typesNotInGroup = availableTypes.filter(t => !group.competencies.includes(t));

  return (
    <div className="adv-group">
      <div className="adv-group__header">
        <span className="adv-group__label">Group {groupIndex + 1}</span>
        {group.competencies.length > 1 && (
          <OperatorToggle
            value={group.internalOperator}
            onChange={(op) => onChangeOperator(group.id, op)}
            size="normal"
          />
        )}
        {canRemove && (
          <button
            type="button"
            className="adv-group__remove"
            onClick={() => onRemoveGroup(group.id)}
            title="Remove group"
          >
            ×
          </button>
        )}
      </div>

      <div className="adv-group__content">
        {group.competencies.length === 0 ? (
          <div className="adv-group__empty">Add competencies to this group</div>
        ) : (
          <div className="adv-group__tags">
            {group.competencies.map((type, idx) => (
              <div key={type} className="adv-group__tag-wrapper">
                {idx > 0 && (
                  <span className="adv-group__tag-operator">
                    {group.internalOperator.toUpperCase()}
                  </span>
                )}
                <div className="adv-group__tag">
                  <span className="adv-group__tag-text">{type}</span>
                  <button
                    type="button"
                    className="adv-group__tag-remove"
                    onClick={() => onRemoveCompetency(group.id, type)}
                    aria-label={`Remove ${type}`}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="adv-group__add">
          <CompetencyDropdown
            availableTypes={typesNotInGroup}
            supervisedPeople={supervisedPeople}
            onSelect={(type) => onAddCompetency(group.id, type)}
            placeholder="+ Add competency"
          />
        </div>
      </div>
    </div>
  );
}

function QueryExpressionDisplay({ query }: { query: QueryState }) {
  if (query.groups.length === 0) return null;

  const parts: string[] = [];
  query.groups.forEach((group, idx) => {
    if (group.competencies.length === 0) return;

    const groupExpr = group.competencies.length === 1
      ? group.competencies[0]
      : `(${group.competencies.join(` ${group.internalOperator.toUpperCase()} `)})`;

    if (idx > 0 && idx - 1 < query.connectors.length) {
      parts.push(query.connectors[idx - 1].toUpperCase());
    }
    parts.push(groupExpr);
  });

  if (parts.length === 0) return null;

  return (
    <div className="adv-expression">
      <span className="adv-expression__label">Query:</span>
      <code className="adv-expression__code">{parts.join(" ")}</code>
    </div>
  );
}

function AdvancedSearchView({
  supervisedPeople,
  uniqueCompetencyTypes,
  query,
  onQueryChange
}: {
  supervisedPeople: SupervisedPerson[];
  uniqueCompetencyTypes: string[];
  query: QueryState;
  onQueryChange: (query: QueryState) => void;
}) {
  const addGroup = useCallback(() => {
    onQueryChange({
      groups: [...query.groups, { id: generateId(), competencies: [], internalOperator: "or" }],
      connectors: [...query.connectors, "and"]
    });
  }, [query, onQueryChange]);

  const removeGroup = useCallback((groupId: string) => {
    const idx = query.groups.findIndex(g => g.id === groupId);
    if (idx === -1) return;

    const newGroups = query.groups.filter(g => g.id !== groupId);
    const newConnectors = [...query.connectors];

    if (idx > 0) {
      newConnectors.splice(idx - 1, 1);
    } else if (newConnectors.length > 0) {
      newConnectors.shift();
    }

    onQueryChange({ groups: newGroups, connectors: newConnectors });
  }, [query, onQueryChange]);

  const addCompetencyToGroup = useCallback((groupId: string, type: string) => {
    onQueryChange({
      ...query,
      groups: query.groups.map(g =>
        g.id === groupId && !g.competencies.includes(type)
          ? { ...g, competencies: [...g.competencies, type] }
          : g
      )
    });
  }, [query, onQueryChange]);

  const removeCompetencyFromGroup = useCallback((groupId: string, type: string) => {
    onQueryChange({
      ...query,
      groups: query.groups.map(g =>
        g.id === groupId
          ? { ...g, competencies: g.competencies.filter(c => c !== type) }
          : g
      )
    });
  }, [query, onQueryChange]);

  const changeGroupOperator = useCallback((groupId: string, operator: "and" | "or") => {
    onQueryChange({
      ...query,
      groups: query.groups.map(g =>
        g.id === groupId ? { ...g, internalOperator: operator } : g
      )
    });
  }, [query, onQueryChange]);

  const changeConnector = useCallback((index: number, operator: "and" | "or") => {
    const newConnectors = [...query.connectors];
    newConnectors[index] = operator;
    onQueryChange({ ...query, connectors: newConnectors });
  }, [query, onQueryChange]);

  const clearAll = useCallback(() => {
    onQueryChange({
      groups: [{ id: generateId(), competencies: [], internalOperator: "or" }],
      connectors: []
    });
  }, [onQueryChange]);

  const matchingPeople = useMemo(() => {
    const nonEmptyGroups = query.groups.filter(g => g.competencies.length > 0);
    if (nonEmptyGroups.length === 0) return [];

    return supervisedPeople.filter(person => {
      const validCompTypes = new Set(
        person.competencies
          .filter(c => !c.isExpired && !c.parentId)
          .map(c => c.type)
      );

      const groupResults: boolean[] = nonEmptyGroups.map(group => {
        if (group.internalOperator === "and") {
          return group.competencies.every(c => validCompTypes.has(c));
        } else {
          return group.competencies.some(c => validCompTypes.has(c));
        }
      });

      let result = groupResults[0];
      for (let i = 1; i < groupResults.length; i++) {
        const connectorIndex = query.groups.findIndex(g => g === nonEmptyGroups[i]) - 1;
        const connector = connectorIndex >= 0 && connectorIndex < query.connectors.length
          ? query.connectors[connectorIndex]
          : "and";

        if (connector === "and") {
          result = result && groupResults[i];
        } else {
          result = result || groupResults[i];
        }
      }

      return result;
    }).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [supervisedPeople, query]);

  const hasAnyCompetencies = query.groups.some(g => g.competencies.length > 0);

  return (
    <div className="adv-search-container">
      <div className="adv-search-builder">
        <div className="adv-groups-container">
          {query.groups.map((group, idx) => (
            <div key={group.id} className="adv-group-wrapper">
              {idx > 0 && (
                <div className="adv-connector">
                  <div className="adv-connector__line" />
                  <OperatorToggle
                    value={query.connectors[idx - 1] || "and"}
                    onChange={(op) => changeConnector(idx - 1, op)}
                    size="large"
                  />
                  <div className="adv-connector__line" />
                </div>
              )}
              <QueryGroupComponent
                group={group}
                groupIndex={idx}
                availableTypes={uniqueCompetencyTypes}
                supervisedPeople={supervisedPeople}
                onAddCompetency={addCompetencyToGroup}
                onRemoveCompetency={removeCompetencyFromGroup}
                onChangeOperator={changeGroupOperator}
                onRemoveGroup={removeGroup}
                canRemove={query.groups.length > 1}
              />
            </div>
          ))}
        </div>

        <div className="adv-actions">
          <button
            type="button"
            className="adv-btn adv-btn--add-group"
            onClick={addGroup}
          >
            + Add Group
          </button>
          {hasAnyCompetencies && (
            <button
              type="button"
              className="adv-btn adv-btn--clear"
              onClick={clearAll}
            >
              Clear All
            </button>
          )}
        </div>

        <QueryExpressionDisplay query={query} />
      </div>

      <div className="adv-search-results">
        {!hasAnyCompetencies ? (
          <div className="adv-instructions">
            <p><strong>How to use:</strong></p>
            <ul>
              <li>Add competencies to a group using the dropdown</li>
              <li>Toggle between AND/OR within each group</li>
              <li>Add more groups and connect them with AND/OR</li>
              <li>Example: <code>(C1 OR C2) AND (C3 OR C4) OR C5</code></li>
            </ul>
            <p><em>Note: Expired competencies are automatically excluded from search results.</em></p>
          </div>
        ) : (
          <>
            <div className="adv-results-header">
              <span className="adv-results-count">
                {matchingPeople.length} {matchingPeople.length === 1 ? "person" : "people"} found
              </span>
            </div>
            {matchingPeople.length === 0 ? (
              <div className="adv-results-empty">
                No personnel match the current query (excluding expired competencies)
              </div>
            ) : (
              <div className="adv-results-list">
                {matchingPeople.map(person => (
                  <div key={person.id} className="adv-results-item">
                    {person.name}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function CompetenciesPage(): JSX.Element {
  const { supervisedData, competenciesData, rootAppData, currentPersonnelId, isSupervisor } = useDataContext();
  const { 
    competenciesPrefs, 
    setCompetenciesViewMode,
    setCompetenciesSelectedPerson, 
    setCompetenciesSearch,
    competenciesSearchPrefs,
    setCompetenciesSearchSelectedType,
    setCompetenciesSearchSelectedTypes,
    competenciesSearchAdvPrefs,
    setCompetenciesSearchAdvQuery
  } = useViewPreferences();
  
  const { viewMode, selectedPersonId, competencySearch } = competenciesPrefs;
  const { selectedCompetencyType, selectedCompetencyTypes } = competenciesSearchPrefs;
  const { query } = competenciesSearchAdvPrefs;

  // Combine supervised data with competencies data (for the root user)
  const combinedSupervisedData = useMemo(() => {
    if (!supervisedData) return null;
    
    const supervisedRows = extractRows(supervisedData);
    const competenciesRows = extractRows(competenciesData);
    const rootPerson = extractRootPerson(rootAppData);
    
    const rootId = rootPerson ? pickString(rootPerson, ["Id", "PersonnelId"]) : null;
    
    if (rootId && competenciesRows.length > 0) {
      const existingPerson = supervisedRows.find(p => {
        const pId = pickString(p, ["Id", "PersonnelId"]);
        return pId?.toLowerCase() === rootId.toLowerCase();
      });
      
      if (existingPerson) {
        const existingComps = Array.isArray(existingPerson["Competencies"]) 
          ? existingPerson["Competencies"] as unknown[]
          : [];
        const existingCompIds = new Set(
          existingComps
            .filter(isRecord)
            .map(c => pickString(c as UnknownRecord, ["PersonnelCompetencyId", "Id"]))
            .filter(Boolean)
        );
        
        const newComps = competenciesRows.filter(c => {
          const compId = pickString(c, ["PersonnelCompetencyId", "Id"]);
          return compId && !existingCompIds.has(compId);
        });
        
        if (newComps.length > 0) {
          const updatedRows = supervisedRows.map(p => {
            const pId = pickString(p, ["Id", "PersonnelId"]);
            if (pId?.toLowerCase() === rootId.toLowerCase()) {
              return {
                ...p,
                Competencies: [...existingComps, ...newComps]
              };
            }
            return p;
          });
          return { data: { value: updatedRows } };
        }
      } else {
        const rootUserEntry = {
          ...rootPerson,
          Competencies: competenciesRows
        };
        return { data: { value: [rootUserEntry, ...supervisedRows] } };
      }
    } else if (rootId && rootPerson) {
      const existingPerson = supervisedRows.find(p => {
        const pId = pickString(p, ["Id", "PersonnelId"]);
        return pId?.toLowerCase() === rootId.toLowerCase();
      });
      
      if (!existingPerson) {
        const rootUserEntry = {
          ...rootPerson,
          Competencies: []
        };
        return { data: { value: [rootUserEntry, ...supervisedRows] } };
      }
    }
    
    return supervisedData;
  }, [supervisedData, competenciesData, rootAppData]);
  
  // Parse supervised data from context
  const supervisedPeople = useMemo(() => {
    if (!combinedSupervisedData) return [];
    return parseSupervisedData(combinedSupervisedData, DEFAULT_PERSONNEL_ID);
  }, [combinedSupervisedData]);

  // Set default selected person when data loads
  useEffect(() => {
    if (supervisedPeople.length > 0) {
      const currentExists = selectedPersonId && supervisedPeople.some(p => p.id === selectedPersonId);
      if (!currentExists) {
        setCompetenciesSelectedPerson(supervisedPeople[0].id);
      }
    }
  }, [supervisedPeople, selectedPersonId, setCompetenciesSelectedPerson]);

  // When not in supervisor mode, use the settings-selected person directly
  const effectivePersonId = isSupervisor ? selectedPersonId : currentPersonnelId;

  // Get unique competency types for search views (only top-level, non-expired)
  const uniqueCompetencyTypes = useMemo(() => {
    const typeSet = new Set<string>();
    supervisedPeople.forEach(person => {
      person.competencies.forEach(comp => {
        if (comp.type && comp.type !== "—" && !comp.isExpired && !comp.parentId) {
          typeSet.add(comp.type);
        }
      });
    });
    return Array.from(typeSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [supervisedPeople]);

  // Select first competency type by default for search view
  useEffect(() => {
    if (uniqueCompetencyTypes.length > 0 && !selectedCompetencyType) {
      setCompetenciesSearchSelectedType(uniqueCompetencyTypes[0]);
    }
  }, [uniqueCompetencyTypes, selectedCompetencyType, setCompetenciesSearchSelectedType]);

  // Handle adding competency in search view
  const handleAddCompetency = useCallback(() => {
    if (selectedCompetencyType && !selectedCompetencyTypes.includes(selectedCompetencyType)) {
      setCompetenciesSearchSelectedTypes([...selectedCompetencyTypes, selectedCompetencyType]);
      const remaining = uniqueCompetencyTypes.filter(
        type => type !== selectedCompetencyType && !selectedCompetencyTypes.includes(type)
      );
      setCompetenciesSearchSelectedType(remaining[0] ?? null);
    }
  }, [selectedCompetencyType, selectedCompetencyTypes, uniqueCompetencyTypes, setCompetenciesSearchSelectedType, setCompetenciesSearchSelectedTypes]);

  const handleRemoveCompetency = useCallback((type: string) => {
    setCompetenciesSearchSelectedTypes(selectedCompetencyTypes.filter(t => t !== type));
  }, [selectedCompetencyTypes, setCompetenciesSearchSelectedTypes]);

  const viewModeLabels: Record<CompetenciesViewMode, string> = {
    list: "List",
    search: "Search",
    advanced: "Advanced"
  };

  return (
    <div className="competencies-page">
      {/* View selector - only shown for supervisors */}
      {isSupervisor && (
        <div className="competencies-view-selector">
          {(["list", "search", "advanced"] as CompetenciesViewMode[]).map(mode => (
            <button
              key={mode}
              type="button"
              className={`competencies-view-selector__btn${viewMode === mode ? " competencies-view-selector__btn--active" : ""}`}
              onClick={() => setCompetenciesViewMode(mode)}
            >
              {viewModeLabels[mode]}
            </button>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <ListView
          supervisedPeople={supervisedPeople}
          selectedPersonId={selectedPersonId}
          effectivePersonId={effectivePersonId}
          competencySearch={competencySearch}
          isSupervisor={isSupervisor}
          onSelectPerson={setCompetenciesSelectedPerson}
          onSearchChange={setCompetenciesSearch}
        />
      )}

      {/* Search View (supervisors only) */}
      {viewMode === "search" && isSupervisor && (
        <SearchView
          supervisedPeople={supervisedPeople}
          uniqueCompetencyTypes={uniqueCompetencyTypes}
          selectedCompetencyType={selectedCompetencyType}
          selectedCompetencyTypes={selectedCompetencyTypes}
          onSelectType={setCompetenciesSearchSelectedType}
          onAddCompetency={handleAddCompetency}
          onRemoveCompetency={handleRemoveCompetency}
        />
      )}

      {/* Advanced Search View (supervisors only) */}
      {viewMode === "advanced" && isSupervisor && (
        <AdvancedSearchView
          supervisedPeople={supervisedPeople}
          uniqueCompetencyTypes={uniqueCompetencyTypes}
          query={query}
          onQueryChange={setCompetenciesSearchAdvQuery}
        />
      )}
    </div>
  );
}
